"use client";

import type { ComponentProps, ComponentType } from "react";

import type {
  LazyDialogInternalProps,
  LazyDialogStateProps,
} from "@/components/shared/LazyDialog";
import {
  AccountSocialCardLazyDialog,
  AddPostLazyDialog,
  BountyRequestLazyDialog,
  BuySubscriptionLazyDialog,
  CashoutAmmLazyDialog,
  ClaimBountyLazyDialog,
  CommentOverlayEditorLazyDialog,
  CreateCollectionLazyDialog,
  CreateCustomFeedLazyDialog,
  CreateListLazyDialog,
  CreateReferralCodeLazyDialog,
  CustomMessageSettingsLazyDialog,
  DraftManagerLazyDialog,
  EditCustomFeedLazyDialog,
  EditListLazyDialog,
  EditorInsertImageLazyDialog,
  EditorInsertLinkLazyDialog,
  EditPostLazyDialog,
  FeedbackLazyDialog,
  FeedManagerLazyDialog,
  FeedSettingsComingSoonLazyDialog,
  FollowLazyDialog,
  FollowUserListsLazyDialog,
  GatedContentOptionsLazyDialog,
  GetTokensEarlyLazyDialog,
  GifSearchLazyDialog,
  HoldersLazyDialog,
  ImageEditorLazyDialog,
  ImageViewerLazyDialog,
  ManageTierLazyDialog,
  MessageMembersLazyDialog,
  MessageTipLazyDialog,
  NewMessageLazyDialog,
  NotificationViewManagerLazyDialog,
  PageShareLazyDialog,
  PayToUnlockMessageLazyDialog,
  PollVotersLazyDialog,
  PostActivityLazyDialog,
  PostOverlayEditorLazyDialog,
  QuotePostLazyDialog,
  ReportPostLazyDialog,
  ReportUserLazyDialog,
  SearchLazyDialog,
  SubscribersLazyDialog,
  TradeCoinLazyDialog,
  TradeCoinMintLazyDialog,
  TradeTransactionSuccessLazyDialog,
  UnlockTokensLazyDialog,
  UpdateEmailLazyDialog,
  UpdateProfileLazyDialog,
  ViewListLazyDialog,
  WalletCashOutLazyDialog,
  WalletCashoutSuccessLazyDialog,
  WalletSendLazyDialog,
  WalletSwapLazyDialog,
  WalletTopUpLazyDialog,
} from "@/components/shared/LazyDialog";
import { DIALOG, type DialogName } from "@/utils/dialog-routes";

export const ROUTED_DIALOG_REGISTRY = {
  [DIALOG.AddPost]: AddPostLazyDialog,
  [DIALOG.FeedManager]: FeedManagerLazyDialog,
  [DIALOG.NotificationViewManager]: NotificationViewManagerLazyDialog,
  [DIALOG.CreateList]: CreateListLazyDialog,
  [DIALOG.EditList]: EditListLazyDialog,
  [DIALOG.ViewList]: ViewListLazyDialog,
  [DIALOG.EditPost]: EditPostLazyDialog,
  [DIALOG.CreateCustomFeed]: CreateCustomFeedLazyDialog,
  [DIALOG.EditCustomFeed]: EditCustomFeedLazyDialog,
  [DIALOG.BuySubscription]: BuySubscriptionLazyDialog,
  [DIALOG.AccountSocialCard]: AccountSocialCardLazyDialog,
  [DIALOG.FeedSettingsComingSoon]: FeedSettingsComingSoonLazyDialog,
  [DIALOG.ImageEditor]: ImageEditorLazyDialog,
  [DIALOG.PostOverlayEditor]: PostOverlayEditorLazyDialog,
  [DIALOG.CommentOverlayEditor]: CommentOverlayEditorLazyDialog,
  [DIALOG.CashoutAmm]: CashoutAmmLazyDialog,
  [DIALOG.ImageViewer]: ImageViewerLazyDialog,
  [DIALOG.BountyRequest]: BountyRequestLazyDialog,
  [DIALOG.GetTokensEarly]: GetTokensEarlyLazyDialog,
  [DIALOG.Holders]: HoldersLazyDialog,
  [DIALOG.Search]: SearchLazyDialog,
  [DIALOG.Follow]: FollowLazyDialog,
  [DIALOG.NewMessage]: NewMessageLazyDialog,
  [DIALOG.MessageMembers]: MessageMembersLazyDialog,
  [DIALOG.Subscribers]: SubscribersLazyDialog,
  [DIALOG.DraftManager]: DraftManagerLazyDialog,
  [DIALOG.GifSearch]: GifSearchLazyDialog,
  [DIALOG.GatedContentOptions]: GatedContentOptionsLazyDialog,
  [DIALOG.MessageTip]: MessageTipLazyDialog,
  [DIALOG.PayToUnlockMessage]: PayToUnlockMessageLazyDialog,
  [DIALOG.EditorInsertImage]: EditorInsertImageLazyDialog,
  [DIALOG.EditorInsertLink]: EditorInsertLinkLazyDialog,
  [DIALOG.TradeTransactionSuccess]: TradeTransactionSuccessLazyDialog,
  [DIALOG.WalletCashoutSuccess]: WalletCashoutSuccessLazyDialog,
  [DIALOG.CustomMessageSettings]: CustomMessageSettingsLazyDialog,
  [DIALOG.PollVoters]: PollVotersLazyDialog,
  [DIALOG.PostActivity]: PostActivityLazyDialog,
  [DIALOG.QuotePost]: QuotePostLazyDialog,
  [DIALOG.FollowUserLists]: FollowUserListsLazyDialog,
  [DIALOG.PageShare]: PageShareLazyDialog,
  [DIALOG.CreateCollection]: CreateCollectionLazyDialog,
  [DIALOG.CreateReferralCode]: CreateReferralCodeLazyDialog,
  [DIALOG.ReportPost]: ReportPostLazyDialog,
  [DIALOG.ReportUser]: ReportUserLazyDialog,
  [DIALOG.ManageTier]: ManageTierLazyDialog,
  [DIALOG.UpdateEmail]: UpdateEmailLazyDialog,
  [DIALOG.UpdateProfile]: UpdateProfileLazyDialog,
  [DIALOG.Feedback]: FeedbackLazyDialog,
  [DIALOG.ClaimBounty]: ClaimBountyLazyDialog,
  [DIALOG.UnlockTokens]: UnlockTokensLazyDialog,
  [DIALOG.WalletTopUp]: WalletTopUpLazyDialog,
  [DIALOG.WalletSwap]: WalletSwapLazyDialog,
  [DIALOG.WalletCashOut]: WalletCashOutLazyDialog,
  [DIALOG.WalletSend]: WalletSendLazyDialog,
  [DIALOG.TradeCoinMint]: TradeCoinMintLazyDialog,
  [DIALOG.TradeCoin]: TradeCoinLazyDialog,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as const satisfies Record<DialogName, ComponentType<any>>;

export type RoutedDialogName = DialogName;

type RoutedDialogComponent<Name extends RoutedDialogName> =
  (typeof ROUTED_DIALOG_REGISTRY)[Name];

type RoutedDialogComponentProps<Name extends RoutedDialogName> = ComponentProps<
  RoutedDialogComponent<Name>
>;

export type DialogProps<Name extends RoutedDialogName> = Omit<
  RoutedDialogComponentProps<Name>,
  keyof LazyDialogInternalProps | keyof LazyDialogStateProps
>;
